import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { isValidDnsName, isValidIPv4, isValidIPv6 } from './dns-validation.utils';

export class DnsRecordFormValidator {
  static create(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const form = control as FormGroup;

      this.validateHost(form);
      this.validateTarget(form);
      this.validatePriority(form);

      return null;
    };
  }

  private static validateHost(form: FormGroup): void {
    const type = form.get('type')?.value;
    const host = form.get('host');

    if (!host) {
      return;
    }

    this.removeErrors(host, ['invalidHost']);

    if (!host.value) {
      return;
    }

    const allowApex = type !== 'CNAME';

    if (!isValidDnsName(host.value, allowApex)) {
      this.addError(host, 'invalidHost', `Please enter a valid ${type} record name.`);
    }
  }

  private static validateTarget(form: FormGroup): void {
    const type = form.get('type')?.value;
    const target = form.get('target');

    if (!target) {
      return;
    }

    this.removeErrors(target, ['required', 'invalidIPv4', 'invalidIPv6', 'invalidDnsName']);

    if (!target.value) {
      this.addError(target, 'required', 'Target has to have a value');
      return;
    }

    switch (type) {
      case 'A':
        if (!isValidIPv4(target.value)) {
          this.addError(target, 'invalidIPv4', 'Please enter a valid IPv4 address');
        }
        break;

      case 'AAAA':
        if (!isValidIPv6(target.value)) {
          this.addError(target, 'invalidIPv6', 'Please enter a valid IPv6 address');
        }
        break;

      case 'CNAME':
      case 'MX':
      case 'NS':
      case 'PTR':
        if (!isValidDnsName(target.value)) {
          this.addError(target, 'invalidDnsName', 'Target must be a valid DNS name.');
        }
        break;

      case 'SRV':
        if (!isValidDnsName(target.value, false, true)) {
          this.addError(target, 'invalidDnsName', 'Target must be a valid DNS name.');
        }
        break;
    }
  }

  private static validatePriority(form: FormGroup): void {
    const type = form.get('type')?.value;
    const priority = form.get('priority');

    if (!priority) {
      return;
    }

    this.removeErrors(priority, ['required']);

    const requiresPriority = type === 'MX' || type === 'SRV';

    if (
      requiresPriority &&
      (priority.value === null || priority.value === undefined || priority.value === '')
    ) {
      this.addError(priority, 'required', 'Priority field is required');
    }
  }

  private static addError(control: AbstractControl, key: string, value: unknown): void {
    control.setErrors({
      ...(control.errors ?? {}),
      [key]: value,
    });
  }

  private static removeErrors(control: AbstractControl, keys: string[]): void {
    if (!control.errors) {
      return;
    }

    const errors = { ...control.errors };

    keys.forEach((key) => delete errors[key]);

    control.setErrors(Object.keys(errors).length ? errors : null);
  }
}
